// libs
import React, { useState } from 'react'

// interfaces / constants
import { IRenderAPI } from 'src/components/_libs/popover_lib/popover_lib'
import { CLASS_PREFIX } from 'src/constants/'
import { NO_USER_PROFILE_TYPE } from 'src/constants/profile'
import { FRONTEND_CREATE_PROFILE, FRONTEND_SETTINGS_PATH } from 'src/constants/urls'
import { POPUP_TYPE_MAP } from 'src/interfaces/app-state'

// classes / components
import BlogProfileAdminModal from 'src/components/blog_profile_admin_modal/blog_profile_admin_modal'
import { FeatureFlag } from 'src/components/feature_flag/feature_flag'
import Feedback from 'src/components/feedback/feedback'
import MenuItemSegment from 'src/components/item_segment/layouts/menu/menu'
import PopoverMenu, { callAll } from 'src/components/popover_menu/popover_menu'
import StaticPages from 'src/components/static_pages/static_pages'
import MenuButton from 'src/containers/smart_components/main_menu/main_menu_button'
import UserOnboardingCTA from 'src/containers/smart_components/user_onboarding_cta/user_onboarding_cta'

// helpers
import { useGlobalPermissions } from '@lopo-tech/lopo-api'
import {
  useHideMap,
  useShowLoginModal,
  useShowRegistrationModal
} from 'src/actions/app-state/app-state_hooks'
import { useAuthContext } from 'src/context/authContext'
import { textResources } from 'src/lang/de'
import { useGetAppActivePopupMenu } from 'src/reducers/app-state/app-state_hooks'
import { useSelectCurrentProfile, useSelectIsUserLoggedIn, useSelectUser } from 'src/reducers/user/user_hooks'
import { getSponsorBadge } from 'src/utils/badges'
import { getConfigValue } from 'src/utils/config/config'
import { isBlogProfile, isFullProfile, isIntermediateProfile } from 'src/utils/profile'
import initTracker, { MAIN_MENU } from 'src/utils/reporting/events_tracking/events_tracking'
import { UrlUtils } from 'src/utils/url/url'
import { logOut } from '../../../actions/user/user'
import { SMALL_ITEM_SEGMENT } from '../../../components/item_segment/base/item_segment_base'
import { StoredProfileHelper } from '../../../utils/location_storage/location_storage'
import './main_menu.scss'

const cls: string = CLASS_PREFIX + 'main-menu'
const clsPopover: string = `${cls}__popover`
const clsSeparator = `${cls}__separator`
const clsStatic = `${cls}__static`
const tracking = initTracker(MAIN_MENU)

const { shared: labelsShared, mainMenu: labelsMainMenu } = textResources

interface IProps {
  className?: string;
  popupPosition?: 'left' | 'right';
}

const MainMenu: React.FC<IProps> = ({ className, popupPosition }) => {
  const isLoggedIn = useSelectIsUserLoggedIn()
  const permissions = useGlobalPermissions()
  const { email, profiles } = useSelectUser()
  const lastSelectedProfileId = StoredProfileHelper.getCurrentProfileId()
  const currentProfile = useSelectCurrentProfile()

  // Get avatar from GraphQL data
  const avatar = currentProfile?.avatar
    // eslint-disable-next-line multiline-ternary
    ? {
      standardImage: false, // GraphQL avatar is always custom
      url: currentProfile.avatar.previewUrl
    } : undefined

  // const { enabled: isMessengerEnabled } = useMessenger()
  const isMapShown = useGetAppActivePopupMenu() === POPUP_TYPE_MAP
  // const mailboxCount = useUnreadConversationsCount()
  const profileType = currentProfile?.__typename || NO_USER_PROFILE_TYPE
  const name = (currentProfile && currentProfile.__typename !== 'IntermediateProfile') ? currentProfile.name : undefined
  const sponsorLevel =
    currentProfile && 'shapeSponsorshipLevel' in currentProfile
      ? currentProfile.shapeSponsorshipLevel
      : undefined
  const showLoginModal = useShowLoginModal()
  const showRegistrationModal = useShowRegistrationModal()
  const hideMap = useHideMap()
  const [blogProfileAdminModalShown, setBlogProfileAdminModalShown] = useState(false)
  const [feedbackModalShown, setFeedbackModalShown] = useState(false)

  const showBlogProfileAdminModal = () => {
    setBlogProfileAdminModalShown(true)
  }

  const hideBlogProfileAdminModal = () => {
    setBlogProfileAdminModalShown(false)
  }

  const onShow = (): void => {
    tracking(MAIN_MENU.ACTIONS.OPEN, MAIN_MENU.LABELS.FINISH)
  }

  const closeMap = (): void => {
    isMapShown && hideMap()
  }

  const showFeedbackModal = () => {
    setFeedbackModalShown(true)
  }

  const hideFeedbackModal = () => {
    setFeedbackModalShown(false)
  }

  const renderAdditionalProfiles = () => {
    const profileKeys = Object.keys(profiles)
    const moreThanOneProfile = profileKeys.length > 1
    if (!isLoggedIn || !moreThanOneProfile) {
      return null
    }

    return (
      <>
        {profileKeys.map(
          (profileId, key) =>
            lastSelectedProfileId !== profileId && (
              <MenuItemSegment
                key={key}
                navigation={{ onClick: switchProfile(profileId) }}
                label={profiles[profileId].name || ''}
                noBorder
                smallMargin
                height={SMALL_ITEM_SEGMENT}
                hover={true}
              />
            )
        )}
        <li className={clsSeparator} />
      </>
    )
  }

  const { setSelectedProfileId } = useAuthContext()

  const switchProfile = (id: string) => {
    return () => {
      tracking(MAIN_MENU.ACTIONS.SWITCH_PROFILE, MAIN_MENU.LABELS.FINISH, () => {
        setSelectedProfileId(id)
      })
    }
  }

  const renderPopoverChildren = ({ hide }: IRenderAPI) => {
    const profileIdentifier = currentProfile?.slug
    const fullProfile = isFullProfile(profileType)
    const intermediateProfile = isIntermediateProfile(profileType)
    const blogProfile = isBlogProfile(profileType)
    const profileLink = profileIdentifier && UrlUtils.getProfileFrontendPath(profileIdentifier, profileType)

    return (
      <nav className={`${cls} ${clsPopover}`}>
        <ul>

          {/* profile */}
          {fullProfile &&
            <MenuItemSegment
              avatar={{ badge: sponsorLevel && getSponsorBadge(sponsorLevel), imageUrl: avatar?.url || '' }}
              navigation={{ onClick: callAll(hide, closeMap), url: profileLink }}
              label={name || ''}
              noBorder
              showArrow={false}
              smallMargin
              height={SMALL_ITEM_SEGMENT}
              hover={true}
            />
          }

          {blogProfile && <MenuItemSegment
            navigation={{ onClick: showBlogProfileAdminModal }}
            label={textResources.manageBlogProfileAdmin.menuName}
            noBorder
            smallMargin
            height={SMALL_ITEM_SEGMENT}
            hover={true}
          />}
          <FeatureFlag active={getConfigValue('featureFlags.startMenu.createProfile')}>
            {intermediateProfile && permissions.canCreateBlogProfile
              ? <UserOnboardingCTA active={true} gtmSourceName='profileMenu--createProfile'>
                <MenuItemSegment
                  navigation={{ onClick: hide, url: FRONTEND_CREATE_PROFILE }}
                  label={labelsMainMenu.createBlog}
                  noBorder
                  smallMargin
                  height={SMALL_ITEM_SEGMENT}
                  hover={true}
                />
              </UserOnboardingCTA>
              : permissions && (permissions.canCreateBlogProfile || permissions.canCreatePrivateProfile)
                ? <MenuItemSegment
                  navigation={{ onClick: hide, url: FRONTEND_CREATE_PROFILE }}
                  label={labelsMainMenu.createBlog}
                  noBorder
                  smallMargin
                  height={SMALL_ITEM_SEGMENT}
                  hover={true}
                />
                : null
            }
          </FeatureFlag>
          <li className={clsSeparator} />

          {/* additional profiles */}
          {renderAdditionalProfiles()}

          {/* mailbox */}
          {/* TODO: implement canReadMessages Permission when it becomes available */}
          {/* {(isLoggedIn && permissions && permissions.readMessages) && isMessengerEnabled && */}

          {/* {isLoggedIn && isMessengerEnabled &&
            <MenuItemSegment
              navigation={{ onClick: hide, url: FRONTEND_CONVERSATION_PATH }}
              label={labelsMainMenu.conversations}
              badge={{ color: 'gray', type: 'message', value: mailboxCount > 0 ? mailboxCount : undefined }}
              noBorder
              smallMargin
              height={SMALL_ITEM_SEGMENT}
              hover={true}
            />
          } */}

          {/* setting logout */}
          {isLoggedIn && <MenuItemSegment
            navigation={{ onClick: hide, url: FRONTEND_SETTINGS_PATH }}
            label={labelsShared.settings}
            noBorder
            smallMargin
            height={SMALL_ITEM_SEGMENT}
            hover={true}
          />}
          {isLoggedIn && <MenuItemSegment
            navigation={{ onClick: callAll(hide, logOut()) }}
            label={labelsShared.logout}
            noBorder
            smallMargin
            height={SMALL_ITEM_SEGMENT}
            hover={true}
          />}

          {/* login register */}
          {!isLoggedIn && <MenuItemSegment
            navigation={{ onClick: callAll(hide, showLoginModal) }}
            label={labelsShared.login}
            noBorder
            smallMargin
            height={SMALL_ITEM_SEGMENT}
            hover={true}
          />}
          {!isLoggedIn && <MenuItemSegment
            navigation={{ onClick: callAll(hide, showRegistrationModal) }}
            label={labelsShared.register}
            noBorder
            smallMargin
            height={SMALL_ITEM_SEGMENT}
            hover={true}
          />}

          <li className={clsSeparator} />

          {/* actions */}
          <MenuItemSegment
            navigation={{ onClick: callAll(hide, showFeedbackModal) }}
            label={textResources.shared.feedback}
            noBorder
            smallMargin
            height={SMALL_ITEM_SEGMENT}
            hover={true}
          />
          <li className={clsStatic}><StaticPages /></li>
        </ul>
      </nav>
    )
  }

  return (
    <>
      <PopoverMenu
        className={className}
        position={'bottom'}
        align={popupPosition || 'left'}
        trigger={<MenuButton image={avatar?.url || ''} />}
        onShow={onShow}
        isFullHeight={false}
      >{renderPopoverChildren}</PopoverMenu>
      {feedbackModalShown && (
        <Feedback loggedInUserEmail={email} close={hideFeedbackModal} />
      )}
      {blogProfileAdminModalShown && name !== undefined &&
        <BlogProfileAdminModal
          profileName={name}
          onClose={hideBlogProfileAdminModal}
        />
      }
    </>
  )
}

export default MainMenu
